import React from 'react'
import * as styles from './Course.module.scss'
import Button from '../common/Button'
import {
  ArrowRightIcon,
  BookIcon,
  BookmarkIcon,
  ClockIcon,
  PreviewSolidIcon,
  TutorialIcon
} from '@atlas-co/icons'
import { GridItem } from './Grid'
import { Link } from 'gatsby'
import Marked from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'

const encodeMarkdownURIs = (source = '') => {
  const markdownLinkRegex = /\[(.+)\]\((.+)(".+)\)/g
  return source?.replace(markdownLinkRegex, (match, linkURI) => {
    if (!linkURI) return match
    const replaced = match.replace(linkURI, encodeURI(linkURI))
    return replaced
  })
}

const calculateTotalDuration = (lessons) => {
  const totalDuration = lessons.reduce((acc, lesson) => {
    const durationStr =
      typeof lesson.duration === 'string'
        ? lesson.duration
        : `${lesson.duration}`
    const duration = parseInt(durationStr.replace(' mins', ''), 10)
    return acc + (isNaN(duration) ? 0 : duration)
  }, 0)

  if (totalDuration >= 60) {
    const hours = Math.floor(totalDuration / 60)
    const minutes = totalDuration % 60
    const hourLabel = hours === 1 ? 'hour' : 'hours'
    const minuteLabel = minutes === 1 ? 'min' : 'mins'
    return minutes > 0
      ? `${hours} ${hourLabel} ${minutes} ${minuteLabel}`
      : `${hours} ${hourLabel}`
  }
  const minuteLabel = totalDuration === 1 ? 'min' : 'mins'
  return `${totalDuration} ${minuteLabel}`
}

const Course = ({ courseData }) => {
  if (!courseData || !courseData.lessons || courseData.lessons.length === 0) {
    return (
      <div className={styles.section}>
        <div className={styles.content}>
          <h1>{courseData?.title || 'Course'}</h1>
          <p>No lessons available for this course.</p>
        </div>
      </div>
    )
  }
  
  const firstLesson = courseData.lessons[0]
  const numberOfLessons = courseData.lessons.length
  const totalDuration = calculateTotalDuration(courseData.lessons)
  
  if (!firstLesson || !firstLesson.slug) {
    return (
      <div className={styles.section}>
        <div className={styles.content}>
          <h1>{courseData.title}</h1>
          <p>Course data is incomplete.</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.section}>
      <div className={styles.headerBackground}>
        <div className={styles.content}>
          <h1>{courseData.title}</h1>
          <div className={styles.meta}>
            <TutorialIcon size={24} />
            <span>{numberOfLessons} lessons</span>
            <span style={{ color: '#b6bbbe' }}>|</span>
            <ClockIcon size={24} />
            <span>{totalDuration}</span>
          </div>
          <div className={styles.headerContent}>
            <div>
              <Link
                to={`/academy/${courseData.slug.current}/${firstLesson.slug.current}/`}
              >
                <div className={styles.image}>
                  <div className={styles.playContainer}>
                    <PreviewSolidIcon size={48} color="#fff" />
                  </div>
                  <img
                    src={courseData.featuredImage.asset.url}
                    alt={courseData.title}
                  />
                </div>
              </Link>
              <div className={styles.courseDetails}>
                <div className={styles.courseDetailsHeader}>
                  <BookmarkIcon size={28} />
                  <span>Course Details</span>
                </div>
                <div className={styles.courseDetailsContent}>
                  <h2>{courseData.title}</h2>
                  <Marked
                    remarkPlugins={[remarkGfm]}
                    rehypePlugins={[rehypeRaw]}
                  >
                    {encodeMarkdownURIs(courseData.longDescription)}
                  </Marked>
                  <Button
                    href={`/academy/${courseData.slug.current}/${firstLesson.slug.current}/`}
                    variant="primary"
                    rightIcon={<ArrowRightIcon color="#fff" />}
                  >
                    First lesson
                  </Button>
                </div>
              </div>
            </div>
            <Toc
              courseSlug={courseData.slug.current}
              lessons={courseData.lessons}
              withBg
            />
          </div>
        </div>
      </div>
    </div>
  )
}

export default Course

export const Toc = ({ courseSlug, lessons, active, withBg }) => {
  if (!lessons || lessons.length === 0) {
    return null
  }
  
  return (
    <div className={`${styles.toc} ${withBg && styles.withBg}`}>
      <h3>Course Content</h3>
      <div className={styles.lessonList}>
        {lessons.map((lesson) => {
          const isActive = active === lesson.slug?.current
          const lessonSlug = lesson.slug?.current || lesson.slug
          return (
            <Link key={lesson.id || lesson._id} to={`/academy/${courseSlug}/${lessonSlug}/`}>
              <div className={styles.lesson}>
                <div
                  className={`${styles.lessonDetails} ${isActive && styles.active}`}
                >
                  <TutorialIcon size={28} />
                  <span>{lesson.title}</span>
                </div>
                {isActive ? (
                  <span className={styles.currentLesson}>Current lesson</span>
                ) : (
                  <span className={styles.minEstimate}>
                    {lesson.duration} {lesson.duration === 1 ? 'min' : 'mins'}
                  </span>
                )}
              </div>
            </Link>
          )
        })}
      </div>
    </div>
  )
}

export const OtherCourses = ({}) => {
  return (
    <div className={styles.moreCourses}>
      <h2>Other courses</h2>
      <div className={styles.otherCoursesGrid}>
        <GridItem
          title="Environmental Modeling with GIS"
          excerpt="Learn how to use Atlas to create custom maps and visualizations"
          short="Intermediate"
          image="https://cdn.sanity.io/images/0xishpzy/production/b82ea8f5160daf6c78ba89aec6f85271ce3c1165-1920x1080.jpg"
        />
        <GridItem
          title="Analyzing Spatial Relationships"
          excerpt="Learn how to use Atlas to create custom maps and visualizations"
          short="Intermediate"
          image="https://cdn.sanity.io/images/0xishpzy/production/fb4df471a8242c041e48b7c45e5d0ae6c4cb4f0c-1920x1080.png"
        />
      </div>
    </div>
  )
}
